// =====================================================================
// Club Radar V2 — design tokens (DESIGN-SPEC.md §1 "Global tokens",
// verbatim values from the handoff `Club Radar.dc.html`).
//
// Scope: CLUB radar surfaces ONLY (frames 1a–1q, 2a–2d). Never import
// these into agent-radar or other surfaces — no global restyling.
// The artboard wins (Brief Rule 1); keep in sync with DESIGN-SPEC.md.
//
// Convention mirrors the sibling V2 kits (agency/v2/tokens.js,
// lawyer/v2/tokens.js): exact px/hex values, exported once, components
// inline them; this module is the single reference.
// =====================================================================

export const COLOR = {
  // Brand
  brand: "#2283FF",
  brandDeep: "#1B6FE0", // hover / deep blue (profile CTA bg, tinted fg)
  tint: "#E8F1FF", // brand tint bg (active rows, pills, banners)
  tintBorder: "#D6E7FF", // active filter-chip border
  bannerBorder: "#CFE3FF", // recommender banner border
  dashedBlue: "#93C5FD", // dashed "new list" checkbox box

  // Surfaces
  page: "#F8FAFC",
  card: "#FFFFFF",
  cardBorder: "#E5E7EB",
  hairline: "#F1F5F9", // row dividers, menu dividers
  softBorder: "#EEF2F6", // note bubble border, info panel border
  sheetRowBorder: "#F8FAFC", // mobile sheet row divider (1m/1o)

  // Text
  text: "#111827",
  heading: "#0F172A",
  bodyStrong: "#374151",
  mid: "#4B5563",
  secondary: "#6B7280",
  muted: "#9CA3AF",
  faint: "#94A3B8",
  slate: "#64748B",
  avatarFg: "#475569",
  chevron: "#CBD5E1", // chevrons, dashed pill borders, event dots

  // Status hues (artboard M map — see STATUS_STYLES below)
  success: "#16A34A",
  successBg: "#ECFDF5",
  successText: "#047857",
  successBorder: "#A7F3D0",
  warningText: "#B45309",
  warningBg: "#FEF3C7",
  danger: "#DC2626",
  dangerBg: "#FEE2E2",

  // Neutrals
  neutralPillBg: "#F3F4F6",
  neutralPillFg: "#4B5563",
  avatarBg: "#EEF2F7",
  inputBg: "#F8FAFC",
  uncheckedBorder: "#D1D5DB",
  reportsDashed: "#D8DEE7",
  reportsBg: "#FBFCFE",
  emptyRing: "#BFDBFE", // S2 radar illustration rings only
};

// Radii scale (DESIGN-SPEC "Radii") — px unless noted.
export const RADIUS = {
  card: 16, // cards / tables / modals / picker popover
  modal: 16,
  sheet: "20px 20px 0 0", // bottom sheets
  inner: 12, // inner containers, menus, banners, tier rows
  control: 10, // buttons / inputs / nav rows (10–12 per frame)
  iconBtnSm: 8, // small icon buttons (8–9)
  iconBtn: 9,
  pill: 999,
  checkbox: 6, // desktop checkbox (mobile 7)
  checkboxMobile: 7,
  flag: 2.5, // flags 2.5–3
  flagLg: 3,
  crest: 4, // crests 4–5
  crestLg: 5,
};

export const SHADOW = {
  frame: "0 10px 30px rgba(15,23,42,.10)",
  menu: "0 14px 34px rgba(15,23,42,.16)",
  menuBoard: "0 14px 34px rgba(15,23,42,.12)", // 2d boards variant
  picker: "0 22px 48px rgba(15,23,42,.20)",
  modal: "0 28px 60px rgba(15,23,42,.30)",
  sheet: "0 -12px 40px rgba(15,23,42,.22)",
  flagRing: "inset 0 0 0 1px rgba(17,24,39,.12)", // flag chip inner ring
  ctaRing: "0 0 0 3px #E8F1FF", // pressed "+ Add to Radar" ring (1l)
};

export const SCRIM = {
  modal: "rgba(15,23,42,.40)", // desktop modal
  sheet: "rgba(15,23,42,.44)", // mobile bottom sheet
};

// Inter (weights 400/500/600/700/800), antialiased; the artboard's
// stack. Applied by the surface builders on their page containers.
export const FONT_FAMILY = "'Inter', system-ui, sans-serif";
export const FONT_WEIGHTS = [400, 500, 600, 700, 800];

// ── Status styling (artboard `M` map) ────────────────────────────────
// DATA-DRIVEN, never a closed enum: labels come from the platform's
// existing status meta (constants/clubRadarChoices.js CLUB_STATUS_META /
// the swappable `statusLabels` set — e.g. Tracking/Shortlisted/Passed);
// this map only styles known keys and falls back for unknown ones.
//
// NOTE the platform ships FOUR statuses (watching · recommended ·
// rejected · shortlisted — documented deviation, clubRadarChoices.js).
// The artboard defines colors for three; `shortlisted` gets the
// artboard's amber pair to stay distinct from watching-blue. Swappable
// via the `styles` prop on StatusPill — flagged for the Lead in the PR.
export const STATUS_STYLES = {
  watching: { bg: "#E8F1FF", fg: "#1B6FE0" },
  recommended: { bg: "#ECFDF5", fg: "#047857" },
  rejected: { bg: "#FEE2E2", fg: "#DC2626" },
  shortlisted: { bg: "#FEF3C7", fg: "#B45309" },
};

// Safe lookup — unknown/absent status keys style as watching (mirrors
// the platform's clubStatusMeta fallback; never throws on new keys).
export const statusStyle = (key, styles = STATUS_STYLES) =>
  styles[key] || styles.watching || STATUS_STYLES.watching;

// ── Member avatar palette (1b/2a/2b/2c stacks & rows) ────────────────
// The artboard's per-person assignments (LF/AK/JW/TR/NR) are SAMPLE
// data; real members rotate deterministically through the same pairs.
export const MEMBER_AVATAR_PAIRS = [
  { bg: "#DBEAFE", fg: "#1D4ED8" }, // LF blue
  { bg: "#FCE7F3", fg: "#BE185D" }, // AK pink
  { bg: "#ECFDF5", fg: "#047857" }, // JW green
  { bg: "#FEF3C7", fg: "#B45309" }, // TR amber
  { bg: "#E8F1FF", fg: "#1B6FE0" }, // NR brand
];

export const memberAvatarPair = (seed) => {
  const s = String(seed || "");
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = s.charCodeAt(i) + ((hash << 5) - hash);
  }
  return MEMBER_AVATAR_PAIRS[Math.abs(hash) % MEMBER_AVATAR_PAIRS.length];
};
